import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  ChartContainer
} from "@/components/ui/chart";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer
} from "recharts";

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

interface PatientCountData {
  name: string;
  patients: number;
}

interface AppointmentsChartData {
  name: string;
  appointments: number;
}

interface PatientChartsProps {
  patientCountData: PatientCountData[];
  appointmentsChartData: AppointmentsChartData[];
  weeklyDistribution: { day: string; count: number }[];
}

export function PatientCharts({ 
  patientCountData, 
  appointmentsChartData,
  weeklyDistribution 
}: PatientChartsProps) {
  // Check if there's data to display
  const hasData = patientCountData.length > 0 || appointmentsChartData.length > 0;
  
  if (!hasData) {
    return (
      <div className="text-center py-12">
        <div className="text-4xl mb-4">👥</div>
        <h3 className="text-lg font-semibold mb-2">No Patient Data Available</h3>
        <p className="text-muted-foreground">
          Patient charts will appear here once you have completed patient consultations.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-6 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Patients by Doctor</CardTitle>
            <CardDescription>Distribution across doctors</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={patientCountData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="patients" fill="#8884d8" name="Patient Count" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Daily Appointments</CardTitle>
            <CardDescription>Patient appointments over time</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={appointmentsChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="appointments" stroke="#82ca9d" name="Appointments" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Weekly Distribution</CardTitle>
            <CardDescription>Patient visits by day of week</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[200px]">
              {weeklyDistribution && weeklyDistribution.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={weeklyDistribution}
                      dataKey="count"
                      nameKey="day"
                      cx="50%"
                      cy="50%"
                      outerRadius={60}
                      label
                    >
                      {weeklyDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <p className="text-muted-foreground text-sm">No weekly data</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
